/* Shared genre drawer (BW-258) - the Category > Genre > Sub-genre slide-out.
 * One component, two callers:
 *   - assets/walkable-2.js's Browse panel (beam's Browse pill on desktop,
 *     the mobile Shelves tab) - "nav" mode: a leaf with no sub-genres is a
 *     real link straight to its shelf; a leaf WITH sub-genres only reveals
 *     the sub column. Unchanged behaviour from before this file existed.
 *   - assets/watch.js's "Pick a genre" button on the watch-list form -
 *     "picker" mode: nothing here is a link. Every leaf (a genre with no
 *     subs, a sub-genre, or "Shop all <genre>") calls onPick() instead, so
 *     the caller can turn a pick into a watch-list chip.
 *
 * Deliberately stateless: buildShell() builds the DOM once; render() is a
 * pure repaint from a state object the caller owns (browseOpen/browseCat/
 * browseGenre for the scene's central store, a small closure for the watch
 * form) - so each caller keeps its own state model, only the drawer's
 * markup and interaction are shared. Styled by assets/genre-drawer.css.
 */
(function () {
  "use strict";

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  // opts: { title, ariaLabel, categories: [{key, label}], allHref, allLabel,
  //         onClose(), onCategory(key), onAllClick(ev) }
  function buildShell(container, opts) {
    opts = opts || {};
    var root = el("div", "ws-browse");
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-label", opts.ariaLabel || "Browse the catalogue");

    var head = el("div", "ws-browse-head", "<span>" + (opts.title || "Browse") + "</span>");
    var close = el("button", "ws-browse-close", "&times;");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", function (ev) { if (opts.onClose) opts.onClose(ev); });
    head.appendChild(close);
    root.appendChild(head);

    var tabs = el("div", "ws-browse-tabs");
    var tabEls = {};
    (opts.categories || []).forEach(function (cat) {
      var tab = el("button", "ws-browse-tab", cat.label);
      tab.type = "button";
      tab.addEventListener("click", function () { if (opts.onCategory) opts.onCategory(cat.key); });
      tabs.appendChild(tab);
      tabEls[cat.key] = tab;
    });
    root.appendChild(tabs);

    var cols = el("div", "ws-browse-cols");
    var genreCol = el("div", "ws-browse-col ws-browse-col--genre");
    var subCol = el("div", "ws-browse-col ws-browse-col--sub");
    cols.appendChild(genreCol);
    cols.appendChild(subCol);
    root.appendChild(cols);

    if (opts.allHref) {
      var allLink = el("a", "ws-browse-all", opts.allLabel || "All shelves &rarr;");
      allLink.href = opts.allHref;
      if (opts.onAllClick) allLink.addEventListener("click", opts.onAllClick);
      root.appendChild(allLink);
    }

    container.appendChild(root);
    return { root: root, tabEls: tabEls, genreCol: genreCol, subCol: subCol };
  }

  // state: { open, mobile, cat, genre } - genre/cat are the active category
  // key and active genre's `key`, or null.
  // opts: { mode: "nav" | "picker",
  //         categories: [{key, label, genres: [{key, title, url,
  //           subs: [{title, url}]}]}],
  //         onGenre(key|null)   - a genre-with-subs row was (de)selected,
  //         onPick({category, genre, sub})  - picker mode: a leaf was chosen,
  //         onLeafClick(ev, node)           - nav mode: a plain <a> leaf was
  //           clicked (e.g. to preventDefault() in a demo/no-Shopify page) }
  // `url` is optional per node - a picker-mode caller (the watch taxonomy,
  // which has no collection behind a leaf) simply omits it. A node's
  // `count`, where a caller still carries one, is not rendered (BW-349).
  function render(shell, state, opts) {
    opts = opts || {};
    var mode = opts.mode || "nav";
    shell.root.classList.toggle("is-open", !!state.open);
    shell.root.classList.toggle("ws-browse--mobile", !!state.mobile);
    shell.root.inert = !state.open;
    Object.keys(shell.tabEls).forEach(function (key) {
      shell.tabEls[key].classList.toggle("is-active", key === state.cat);
    });

    var cat = (opts.categories || []).filter(function (c) { return c.key === state.cat; })[0];
    var genres = (cat && cat.genres) || [];
    shell.genreCol.innerHTML = "";
    var activeGenre = null;
    genres.forEach(function (g) {
      var hasSubs = !!(g.subs && g.subs.length);
      var drills = hasSubs && !state.mobile;
      var isActive = g.key === state.genre;
      if (isActive) activeGenre = g;
      var isLink = mode === "nav" && !drills;
      var row = el(isLink ? "a" : "button", "ws-browse-opt" + (isActive ? " is-active" : ""));
      if (isLink) row.href = g.url || "#"; else row.type = "button";
      row.innerHTML = '<span class="ws-browse-optt">' + g.title + "</span>" +
        (drills ? '<span class="ws-chev">&rsaquo;</span>' : "");
      row.addEventListener("click", function (ev) {
        if (drills) {
          // The re-render below wipes and rebuilds genreCol, detaching THIS
          // row before the click finishes bubbling - stop it here, or a
          // document click-outside listener reads the click as "outside"
          // and closes the whole panel instead of opening the sub column.
          ev.preventDefault();
          ev.stopPropagation();
          if (opts.onGenre) opts.onGenre(isActive ? null : g.key);
        } else if (mode === "picker") {
          ev.preventDefault();
          if (opts.onPick) opts.onPick({ category: cat.key, genre: g.title, sub: null });
        } else if (opts.onLeafClick) {
          opts.onLeafClick(ev, g);
        }
      });
      shell.genreCol.appendChild(row);
    });

    shell.subCol.classList.toggle("is-open", !!activeGenre);
    shell.subCol.innerHTML = "";
    if (activeGenre) {
      (activeGenre.subs || []).forEach(function (sub) {
        var isLink = mode === "nav";
        var leaf = el(isLink ? "a" : "button", "ws-browse-opt");
        if (isLink) leaf.href = sub.url || "#"; else leaf.type = "button";
        leaf.innerHTML = '<span class="ws-browse-optt">' + sub.title + "</span>";
        leaf.addEventListener("click", function (ev) {
          if (mode === "picker") {
            ev.preventDefault();
            if (opts.onPick) opts.onPick({ category: cat.key, genre: activeGenre.title, sub: sub.title });
          } else if (opts.onLeafClick) {
            opts.onLeafClick(ev, sub);
          }
        });
        shell.subCol.appendChild(leaf);
      });
      var isLinkAll = mode === "nav";
      var shopAll = el(isLinkAll ? "a" : "button", "ws-browse-opt ws-browse-opt--all", "Shop all " + activeGenre.title);
      if (isLinkAll) shopAll.href = activeGenre.url || "#"; else shopAll.type = "button";
      shopAll.addEventListener("click", function (ev) {
        if (mode === "picker") {
          ev.preventDefault();
          if (opts.onPick) opts.onPick({ category: cat.key, genre: activeGenre.title, sub: null });
        } else if (opts.onLeafClick) {
          opts.onLeafClick(ev, activeGenre);
        }
      });
      shell.subCol.appendChild(shopAll);
    }
  }

  window.BookwormsGenreDrawer = { buildShell: buildShell, render: render };
})();
